'use client';

import { Badge, Checkbox, Text } from '@terra/ui-design-system';
import { Box, Grid, HStack } from '@terra/ui-styled-system';
import { AiApplicationInterfaceTypeSchema } from '@terra/shared-contracts-ai-applications';
import { useTranslations } from 'next-intl';
import type { z } from 'zod';

export type AiApplicationInterfaceType = z.infer<
  typeof AiApplicationInterfaceTypeSchema
>;

const NON_SELECTABLE_PROTOCOLS: AiApplicationInterfaceType[] = ['VOICE', 'MCP'];

type AiApplicationProtocolSelectorProps = {
  selectedProtocols: AiApplicationInterfaceType[];
  onSelectedProtocolsChange: (protocols: AiApplicationInterfaceType[]) => void;
};

export function AiApplicationProtocolSelector({
  selectedProtocols,
  onSelectedProtocolsChange,
}: AiApplicationProtocolSelectorProps) {
  const t = useTranslations('aiApplications');

  const getProtocolLabel = (protocol: AiApplicationInterfaceType) =>
    t(`interfaceTypeOptions.${protocol}`);

  const handleProtocolChange = ({
    protocol,
    isChecked,
  }: {
    protocol: AiApplicationInterfaceType;
    isChecked: boolean;
  }) => {
    if (isChecked) {
      if (selectedProtocols.includes(protocol)) {
        return;
      }
      onSelectedProtocolsChange([...selectedProtocols, protocol]);
      return;
    }

    if (selectedProtocols.length === 1) {
      return;
    }

    onSelectedProtocolsChange(
      selectedProtocols.filter((item) => item !== protocol),
    );
  };

  const selectableProtocols = AiApplicationInterfaceTypeSchema.options.filter(
    (protocol) => !NON_SELECTABLE_PROTOCOLS.includes(protocol),
  );

  return (
    <Grid columns={{ base: 1, md: 2 }} gap={3}>
      {selectableProtocols.map((protocol) => {
        const isChecked = selectedProtocols.includes(protocol);

        return (
          <Box
            key={protocol}
            borderWidth="1px"
            borderColor={isChecked ? 'border.emphasized' : 'border.subtle'}
            borderRadius="lg"
            px={4}
            py={3}
          >
            <Checkbox
              key={protocol}
              ids={{
                root: `ai-protocol-${protocol}`,
                hiddenInput: `ai-protocol-${protocol}-input`,
              }}
              checked={isChecked}
              disabled={isChecked && selectedProtocols.length === 1}
              onCheckedChange={(details) =>
                handleProtocolChange({
                  protocol,
                  isChecked: Boolean(details.checked),
                })
              }
            >
              <Text fontWeight="medium">{getProtocolLabel(protocol)}</Text>
            </Checkbox>
          </Box>
        );
      })}
      <Box
        borderWidth="1px"
        borderColor="border.subtle"
        borderRadius="lg"
        px={4}
        py={3}
        opacity={0.7}
      >
        <HStack gap={2} alignItems="center">
          <Text fontWeight="medium" color="fg.muted">
            {getProtocolLabel('VOICE')}
          </Text>
          <Badge variant="outline" size="sm">
            {t('comingSoonLabel')}
          </Badge>
        </HStack>
      </Box>
    </Grid>
  );
}
